import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  applyTypography,
  BaseButton,
  Checkbox,
  Toast,
  useT,
} from '@repo/common';
import PaymentSummary from '../../components/Kpathport/Booking/PaymentSummary';
import BagItemGroup from '../../components/Kpathport/Booking/BagItemGroup';
import StepIndicator from '../../components/Kpathport/components/StepIndicator';
import RefundModal from '../../components/Kpathport/Modal/RefundModal';
import {
  formatDate,
  formatTime,
} from '../../components/Kpathport/utils/dateTimeUtils';
import { ReactComponent as NavigationLeftIcon20 } from '@repo/common/assets/icons/20px/icon_20px_navigation-left.svg';
import {
  getCartItems,
  removeFromCart,
  removeMultipleFromCart,
  updateCartItemSelection,
  addToCart,
} from '../../api/cart/cart.api';
import { getUserBookings } from '../../api/booking/booking.api';
import { getWorkshopDetail } from '../../api/workshop/workshop.api';
import {
  formatWorkshopPrice,
  isBankTransferMethod,
} from '../../components/Kpathport/utils/priceUtils';
import { combineRefundPolicies } from '../../components/Kpathport/utils/refundPolicyUtils';
import IntakeQuestionsModal from '../../components/Kpathport/Modal/IntakeQuestionsModal';
import useIntakeGate from '../../components/Kpathport/hooks/useIntakeGate';

const blackCat = `${process.env.REACT_APP_ASSETS_URL}/img-logo-black-cat.svg`;

const Booking = () => {
  const t = useT();
  const navigate = useNavigate();
  // 예약 확인으로 넘어가기 전 사전 질문 게이트
  const intakeGate = useIntakeGate();
  const [currentStep] = useState(1);
  const [bagItems, setBagItems] = useState([]);
  const [showRemoveToast, setShowRemoveToast] = useState(false);
  const [removedItems, setRemovedItems] = useState([]);
  const [showRefundModal, setShowRefundModal] = useState(false);

  // 장바구니 데이터를 UI 모델로 변환하는 함수
  const transformCartItems = useCallback(
    (cartItems, pendingPaymentSlotIds = new Set()) => {
      if (!Array.isArray(cartItems)) return [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return cartItems.map((item) => {
        const date = formatDate(item.date);
        const time = formatTime(item.startTime, item.endTime);

        // 위치 정보
        const location =
          item.meetingPointName && item.meetingPointAddress
            ? `${item.meetingPointName}, ${item.meetingPointAddress}`
            : item.meetingPointAddress || item.workshopLocation || '';

        // 가격 처리
        const price = item.isFree ? 0 : item.price ? parseFloat(item.price) : 0;
        const isFree = item.isFree || price === 0;

        // 날짜가 지났는지 확인
        const rawDate = item.date ? new Date(item.date) : null;
        const isExpired = rawDate && !isNaN(rawDate) ? rawDate < today : false;

        const isPendingPaymentSlot = pendingPaymentSlotIds.has(
          String(item.workshopDateSlotId),
        );
        const isDisabled =
          !isPendingPaymentSlot &&
          !isExpired &&
          (!item.available ||
            (item.maxParticipants > 0 &&
              item.currentParticipants >= item.maxParticipants));

        return {
          id: item.id,
          cartItemId: item.id,
          workshopId: item.workshopId,
          workshopDateSlotId: item.workshopDateSlotId,
          name: item.workshopTitle || 'Workshop name',
          image:
            item.images && Array.isArray(item.images) && item.images.length > 0
              ? item.images[0]
              : '',
          location,
          date,
          time,
          price,
          currency: item.currency || 'USD',
          originalPrice: null,
          isFree,
          // 계좌이체 워크숍(Prana Club 한옥 웰니스 등) — 카드 결제 워크숍과 함께 결제할 수 없다
          isBankTransfer: isBankTransferMethod(item.paymentMethod),
          // 이 슬롯에 결제(입금) 대기 중인 예약이 이미 있다 — Pay now 를 누르면 새 예약 대신 그 예약으로 이어진다
          isPendingPayment: isPendingPaymentSlot,
          refundPolicy: item.refundPolicy || '',
          isSelected: item.isSelected !== false,
          isDisabled,
          isExpired,
          groupDate: isDisabled || isExpired ? 'No longer available' : date,
        };
      });
    },
    [],
  );

  const fetchBagItems = useCallback(async () => {
    const [cartResult, bookingsResult] = await Promise.allSettled([
      getCartItems(),
      getUserBookings(),
    ]);
    const cartData = cartResult.status === 'fulfilled' ? cartResult.value : [];
    const bookings =
      bookingsResult.status === 'fulfilled' ? bookingsResult.value : [];
    const pendingPaymentSlotIds = new Set(
      (bookings || [])
        .filter((booking) => {
          const status = String(
            booking?.status ?? booking?.bookingStatus ?? '',
          ).toUpperCase();
          return status.includes('HOLD') || status.includes('OFFERED');
        })
        .map((booking) =>
          String(
            booking?.workshopDateSlotId ??
              booking?.workshopDateSlot?.id ??
              booking?.dateSlotId,
          ),
        ),
    );
    return transformCartItems(cartData, pendingPaymentSlotIds);
  }, [transformCartItems]);

  // 장바구니 데이터 가져오기
  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        setBagItems(await fetchBagItems());
      } catch (error) {
        console.error('Failed to fetch cart items:', error);
        setBagItems([]);
      }
    };

    fetchCartItems();
  }, [fetchBagItems]);

  // 날짜별로 그룹화
  const groupedItems = useMemo(() => {
    const groups = {};
    bagItems.forEach((item) => {
      const dateKey = item.groupDate;
      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: dateKey,
          items: [],
        };
      }
      groups[dateKey].items.push(item);
    });
    const groupedList = Object.values(groups);
    groupedList.sort((a, b) => {
      const isUnavailableA =
        String(a?.date || '').toLowerCase() === 'no longer available';
      const isUnavailableB =
        String(b?.date || '').toLowerCase() === 'no longer available';

      if (isUnavailableA && !isUnavailableB) return 1;
      if (!isUnavailableA && isUnavailableB) return -1;
      return 0;
    });
    return groupedList;
  }, [bagItems]);

  // 결제 가능한 선택 아이템 수 (expired, disabled 제외)
  const selectedCount = useMemo(() => {
    return bagItems.filter(
      (item) => item.isSelected && !item.isDisabled && !item.isExpired,
    ).length;
  }, [bagItems]);

  const selectableItemsCount = bagItems.filter(
    (item) => !item.isDisabled && !item.isExpired,
  ).length;
  const totalItems = bagItems.length;
  const isAllSelected =
    selectedCount === selectableItemsCount && selectableItemsCount > 0;
  const isEmpty = bagItems.length === 0;

  // 전체 선택/해제 (expired, disabled 제외)
  const handleSelectAll = () => {
    setBagItems((prev) =>
      prev.map((item) => ({
        ...item,
        isSelected:
          item.isDisabled || item.isExpired ? item.isSelected : !isAllSelected,
      })),
    );
  };

  // 개별 아이템 선택 토글
  const handleItemToggle = async (itemId) => {
    const item = bagItems.find((i) => i.id === itemId);
    if (!item) return;

    const newSelectedState = !item.isSelected;

    // 낙관적 업데이트
    setBagItems((prev) =>
      prev.map((i) =>
        i.id === itemId ? { ...i, isSelected: newSelectedState } : i,
      ),
    );

    try {
      await updateCartItemSelection(itemId, newSelectedState);
    } catch (error) {
      console.error('Failed to update cart item selection:', error);
      // 실패 시 롤백
      setBagItems((prev) =>
        prev.map((i) =>
          i.id === itemId ? { ...i, isSelected: !newSelectedState } : i,
        ),
      );
      alert('선택 상태를 변경하는데 실패했습니다.');
    }
  };

  // 선택된 아이템 제거 (expired 아이템도 제거 가능)
  const handleRemove = async () => {
    const selectedItems = bagItems.filter((item) => item.isSelected);
    if (selectedItems.length === 0) return;

    const itemIds = selectedItems.map((item) => item.id);

    // 제거 대상 아이템 정보 저장 (Undo 용)
    const itemsToRemove = selectedItems.map((item) => ({
      id: item.id,
      workshopDateSlotId: item.workshopDateSlotId,
      quantity: 1, // 기본 수량
    }));
    setRemovedItems(itemsToRemove);

    // 낙관적 업데이트 (expired 포함 선택된 항목 제거)
    setBagItems((prev) => prev.filter((item) => !item.isSelected));

    try {
      if (itemIds.length === 1) {
        await removeFromCart(itemIds[0]);
      } else {
        await removeMultipleFromCart(itemIds);
      }
      // 성공 시 토스트 표시
      setShowRemoveToast(true);
    } catch (error) {
      console.error('Failed to remove cart items:', error);
      // 실패 시 데이터 재조회
      try {
        setBagItems(await fetchBagItems());
      } catch (fetchError) {
        console.error('Failed to refetch cart items:', fetchError);
      }
      alert('장바구니에서 제거하는데 실패했습니다.');
      setRemovedItems([]);
    }
  };

  // Undo 처리
  const handleUndo = async () => {
    if (removedItems.length === 0) return;

    try {
      // 제거된 아이템을 다시 장바구니에 추가
      for (const item of removedItems) {
        await addToCart(item.workshopDateSlotId, item.quantity);
      }

      // 장바구니 데이터 다시 조회
      setBagItems(await fetchBagItems());

      // 토스트 닫기
      setShowRemoveToast(false);
      setRemovedItems([]);
    } catch (error) {
      console.error('Failed to undo remove:', error);
      alert('아이템을 다시 추가하는데 실패했습니다.');
    }
  };

  // 총 금액 계산 (expired 제외)
  const paymentInfo = useMemo(() => {
    const selectedItems = bagItems.filter(
      (item) => item.isSelected && !item.isDisabled && !item.isExpired,
    );
    let total = 0;
    let originalTotal = 0;

    selectedItems.forEach((item) => {
      if (item.isFree) {
        if (item.originalPrice) {
          originalTotal +=
            typeof item.originalPrice === 'number'
              ? item.originalPrice
              : parseInt(
                  item.originalPrice.toString().replace(/[^0-9]/g, '') || '0',
                );
        }
      } else {
        const price =
          typeof item.price === 'number'
            ? item.price
            : parseInt(item.price.toString().replace(/[^0-9]/g, '') || '0');
        total += price;
        if (item.originalPrice) {
          originalTotal +=
            typeof item.originalPrice === 'number'
              ? item.originalPrice
              : parseInt(
                  item.originalPrice.toString().replace(/[^0-9]/g, '') || '0',
                );
        } else {
          originalTotal += price;
        }
      }
    });

    // 선택된 유료 항목의 통화 (계좌이체 워크숍은 KRW, 카드 결제는 USD). 없으면 첫 항목, 그것도 없으면 USD
    const paidItem = selectedItems.find((item) => !item.isFree);
    const currency = (paidItem || selectedItems[0])?.currency || 'USD';

    return {
      total: total === 0 && originalTotal > 0 ? 'Free' : total,
      originalPrice: originalTotal > total ? originalTotal : null,
      itemCount: selectedItems.length,
      currency,
    };
  }, [bagItems]);

  // 선택된 유료 항목의 결제 방식. 계좌이체와 카드가 섞이면 결제 단계로 못 넘어간다.
  const selectedPaid = useMemo(
    () =>
      bagItems.filter(
        (item) =>
          item.isSelected && !item.isDisabled && !item.isExpired && !item.isFree,
      ),
    [bagItems],
  );
  const hasBankTransferSelected = selectedPaid.some((item) => item.isBankTransfer);
  const hasCardSelected = selectedPaid.some((item) => !item.isBankTransfer);
  // 계좌이체 인보이스는 워크숍 하나 단위다(계좌가 워크숍마다 다르다). 서로 다른 계좌이체 워크숍 둘도 함께 결제 불가
  const bankTransferWorkshopCount = new Set(
    selectedPaid.filter((item) => item.isBankTransfer).map((item) => item.workshopId),
  ).size;
  const isMixedPaymentSelection =
    (hasBankTransferSelected && hasCardSelected) || bankTransferWorkshopCount > 1;
  const isBankTransferOnly = hasBankTransferSelected && !hasCardSelected;

  // 환불 정책 모달 — 선택된 워크숍의 전용 정책을 보여준다. 전용 정책이 하나도 없으면 기본 정책.
  // 선택이 없으면 가방에 담긴 전체를 기준으로 한다.
  const selectedRefundPolicy = useMemo(() => {
    const active = bagItems.filter((item) => !item.isDisabled && !item.isExpired);
    const selected = active.filter((item) => item.isSelected);
    const basis = selected.length > 0 ? selected : active;
    return combineRefundPolicies(
      basis.map((item) => ({
        workshopId: item.workshopId,
        title: item.name,
        refundPolicy: item.refundPolicy,
      })),
    );
  }, [bagItems]);

  const handleBack = () => {
    navigate(-1);
  };

  const handlePayNow = async () => {
    const selectedItems = bagItems.filter(
      (item) => item.isSelected && !item.isDisabled && !item.isExpired,
    );
    if (selectedItems.length === 0) {
      const hasExpiredSelected = bagItems.some(
        (item) => item.isSelected && item.isExpired,
      );
      alert(
        hasExpiredSelected
          ? t(
              'Selected workshops are no longer available. Please remove them.',
              '선택한 워크숍은 더 이상 예약할 수 없습니다. 삭제해주세요.',
            )
          : '결제할 아이템을 선택해주세요.',
      );
      return;
    }

    // 계좌이체 워크숍은 카드 결제 워크숍과 한 번에 결제할 수 없다 (서버도 같은 이유로 거부한다)
    if (isMixedPaymentSelection) {
      alert(
        bankTransferWorkshopCount > 1
          ? t(
              'Bank-transfer workshops are booked one at a time because each has its own account. Please select one of them.',
              '계좌이체 워크숍은 입금 계좌가 워크숍마다 달라 한 번에 하나씩 예약합니다. 하나만 선택해 주세요.',
            )
          : t(
              'This workshop is paid by bank transfer and must be booked separately. Please select it on its own.',
              '계좌이체 워크숍은 다른 워크숍과 함께 예약할 수 없습니다. 따로 선택해 주세요.',
            ),
      );
      return;
    }

    // 선택된 아이템의 cartItemId를 check-details 페이지로 전달
    const cartItemIds = selectedItems.map((item) => item.id);

    /*
     * 사전 질문이 있는 워크숍이면 예약 확인 화면에 들어가기 전에 답을 받는다.
     * 장바구니에는 워크숍이 여러 개 담길 수 있어 하나씩 차례로 묻는다.
     */
    const workshopIds = [
      ...new Set(selectedItems.map((item) => item.workshopId).filter(Boolean)),
    ];
    const workshops = await Promise.all(
      workshopIds.map(async (workshopId) => {
        try {
          return await getWorkshopDetail(workshopId);
        } catch {
          // 상세를 못 불러오면 질문 없는 것으로 보고 진행시킨다. 서버가 한 번 더 막는다
          return null;
        }
      }),
    );

    await intakeGate.runMany(workshops.filter(Boolean), () => {
      navigate(
        `/community/kpathport/check-details?cartItemIds=${cartItemIds.join(',')}`,
      );
    });
  };

  return (
    <>
      <IntakeQuestionsModal {...intakeGate.modalProps} />
      <div className='tabletH:hidden bg-grayscale-gray01 px-[16px] pt-[16px] pb-[8px]'>
        <div className='relative flex items-center justify-between'>
          <BaseButton
            type='text'
            size='s'
            onClick={handleBack}
            className='bg-grayscale-gray01 hover:bg-grayscale-gray01 h-auto min-w-0 border-0 content-stretch p-[14px] relative rounded-[8px] shrink-0 hover:opacity-80 transition-opacity'
            aria-label={t('Go back', '뒤로 가기')}
            icon={
              <div className='content-stretch flex items-center overflow-clip p-[2px] relative shrink-0 w-[20px]'>
                <NavigationLeftIcon20 className='w-full h-full' />
              </div>
            }
          />
          <div className='flex flex-col items-center gap-[8px]'>
            <p
              className={`${applyTypography(
                'title3',
              )} text-text-primary shrink-0`}
              style={{ lineHeight: '1.2' }}
            >
              {t('My bag', '내 가방')}
            </p>
            <StepIndicator
              currentStep={currentStep}
              className='scale-[0.92] origin-center'
            />
          </div>
          <div className='size-[48px] shrink-0' />
        </div>
      </div>
      <div className='relative bg-grayscale-gray01 min-h-screen flex flex-col gap-[16px] px-[24px] pb-[120px] pt-[24px] tabletH:content-start tabletH:flex-row tabletH:flex-wrap tabletH:gap-[24px_0px] tabletH:items-start tabletH:justify-center tabletH:pb-[240px] tabletH:pl-[44px] tabletH:pr-[100px]'>
        {/* Left Section */}
        <div className='content-start flex flex-col w-full tabletH:w-auto tabletH:flex-[1_0_0] tabletH:flex-row tabletH:flex-wrap tabletH:gap-0 tabletH:items-start tabletH:max-w-[778px] tabletH:min-h-px tabletH:min-w-[600px] relative'>
          {/* Back Button */}
          <div className='hidden tabletH:content-stretch tabletH:flex tabletH:h-[70px] tabletH:items-center relative shrink-0'>
            <BaseButton
              type='text'
              size='s'
              onClick={handleBack}
              className='bg-grayscale-gray01 hover:bg-grayscale-gray01 min-w-0 border-0 p-0 content-stretch relative rounded-[50px] shrink-0 size-[32px] hover:opacity-80 transition-opacity'
              aria-label={t('Go back', '뒤로 가기')}
              icon={
                <div className='content-stretch flex items-center overflow-clip p-[2px] relative shrink-0 w-[20px]'>
                  <NavigationLeftIcon20 className='w-full h-full' />
                </div>
              }
            />
          </div>

          {/* Main Content */}
          <div className='content-stretch flex flex-col gap-[16px] items-start relative shrink-0 w-full tabletH:flex-[1_0_0] tabletH:gap-[24px] tabletH:min-h-px tabletH:min-w-[520px] tabletH:pl-[24px]'>
            {/* Header with Steps */}
            <div className='hidden tabletH:content-stretch tabletH:flex tabletH:h-[70px] tabletH:items-center tabletH:justify-between tabletH:overflow-clip tabletH:py-[16px] relative shrink-0 w-full'>
              <p
                className={`${applyTypography(
                  'h7',
                )} text-text-primary shrink-0`}
                style={{ lineHeight: '1.2' }}
              >
                {t('My Bag', '내 가방')}
              </p>
              <StepIndicator currentStep={currentStep} />
            </div>

            {/* Items Section */}
            <div className='content-stretch flex flex-col items-start pb-[24px] relative shrink-0 w-full tabletH:pb-[40px]'>
              {/* Selection Header */}
              <div className='border-b border-grayscale-gray03 content-stretch flex items-center justify-between pb-[16px] tabletH:pb-[24px] relative shrink-0 w-full'>
                <div className='flex flex-row items-center self-stretch'>
                  <div className='relative flex items-center h-full content-stretch shrink-0'>
                    <div className='content-stretch flex h-full items-center pr-[8px] tabletH:pr-[16px] relative shrink-0'>
                      <Checkbox
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        boxClassName={
                          isAllSelected
                            ? 'bg-functional-success03 border-none'
                            : 'bg-grayscale-gray00 border border-grayscale-gray03'
                        }
                      />
                    </div>
                    <p
                      className={`typo-b14Strong text-text-primary shrink-0 tabletH:typo-b16Strong`}
                    >
                      {isEmpty
                        ? t('0/0 items selected', '0/0개 선택됨')
                        : t(
                            `${selectedCount}/${totalItems} items selected`,
                            `${selectedCount}/${totalItems}개 선택됨`,
                          )}
                    </p>
                  </div>
                </div>
                <BaseButton
                  type='text'
                  size='s'
                  hideIcon
                  typo={false}
                  isStatic
                  onClick={handleRemove}
                  textWrapperClass='h-auto'
                  className='h-auto min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 font-["Mona_Sans:Medium",sans-serif] leading-[1.4] text-[14px] tabletH:leading-[1.5] tabletH:text-[16px] text-text-primary shrink-0 hover:opacity-80 transition-opacity'
                >
                  {t('Remove', '삭제')}
                </BaseButton>
              </div>

              {/* Items List or Empty State */}
              {isEmpty ? (
                <div className='content-stretch flex flex-col h-[420px] tabletH:h-[500px] items-center justify-center px-[24px] py-[40px] relative shrink-0 w-full'>
                  <div className='content-stretch flex flex-col items-center pb-[24px] tabletH:pb-[40px] relative shrink-0 w-full'>
                    <div className='content-stretch flex flex-col gap-[8px] items-center not-italic pb-[24px] relative shrink-0 text-text-primary text-center w-full whitespace-pre-wrap tabletH:w-[570px]'>
                      <p
                        className={`${applyTypography(
                          'title2',
                        )} shrink-0 w-full tabletH:w-[570px]`}
                        style={{ lineHeight: '1.2' }}
                      >
                        {t('Ooops', '이런!')}
                      </p>
                      <div
                        className={`${applyTypography(
                          'b16',
                        )} min-w-full relative shrink-0 w-[min-content]`}
                        style={{ lineHeight: '1.5' }}
                      >
                        <p className='mb-0'>
                          {t('Your bag is empty', '가방이 비어 있어요')}
                        </p>
                        <p>
                          {t(
                            "Come on, let's find something to book!",
                            '예약할 워크숍을 찾아볼까요?',
                          )}
                        </p>
                      </div>
                    </div>
                    <div className='relative flex flex-col items-center w-full content-stretch shrink-0'>
                      <BaseButton
                        type='primary'
                        size='xl'
                        hideIcon
                        onClick={() =>
                          navigate('/community/kpathport/pathfeed')
                        }
                      >
                        {t('Browse workshops', '워크숍 둘러보기')}
                      </BaseButton>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='content-stretch flex flex-col items-start pt-[16px] tabletH:pt-[24px] relative shrink-0 w-full'>
                  {groupedItems.map((group) => (
                    <BagItemGroup
                      key={group.date}
                      group={group}
                      onItemToggle={handleItemToggle}
                    />
                  ))}
                </div>
              )}

              {/* Total Price */}
              <div className='hidden tabletH:content-stretch tabletH:flex tabletH:gap-[8px] tabletH:items-center tabletH:justify-end not-italic relative shrink-0 w-full'>
                <div className='relative flex flex-col items-start justify-center content-stretch shrink-0'>
                  <p
                    className={`${applyTypography(
                      'b16Strong',
                    )} text-text-primary shrink-0`}
                    style={{ lineHeight: '1.5' }}
                  >
                    {t('Total :', '총 결제 금액 :')}
                  </p>
                  <p
                    className={`${applyTypography(
                      'b13',
                    )} text-text-placeholder shrink-0`}
                    style={{ lineHeight: '1.4' }}
                  >
                    {t('(Incl. VAT)', '(부가세 포함)')}
                  </p>
                </div>
                <div className='content-stretch flex flex-col font-["Mona_Sans:Medium",sans-serif] items-end justify-center relative shrink-0 text-right'>
                  {paymentInfo.originalPrice && (
                    <p
                      className={`${applyTypography(
                        'b14',
                      )} text-text-placeholder shrink-0 line-through`}
                      style={{ lineHeight: '1.4' }}
                    >
                      {t(
                        formatWorkshopPrice(
                          paymentInfo.originalPrice,
                          paymentInfo.currency,
                        ),
                        formatWorkshopPrice(
                          paymentInfo.originalPrice,
                          paymentInfo.currency,
                        ).replace('won', '원'),
                      )}
                    </p>
                  )}
                  <p
                    className={`${applyTypography('b20Strong')} ${
                      isEmpty ? 'text-text-primary' : 'text-text-accent'
                    } shrink-0`}
                    style={{ lineHeight: '1.5' }}
                  >
                    {isEmpty
                      ? t('0 won', '0원')
                      : paymentInfo.total === 'Free'
                        ? t('Free', '무료')
                        : typeof paymentInfo.total === 'number'
                          ? t(
                              formatWorkshopPrice(
                                paymentInfo.total,
                                paymentInfo.currency,
                              ).replace('won', ' won'),
                              formatWorkshopPrice(
                                paymentInfo.total,
                                paymentInfo.currency,
                              ).replace('won', '원'),
                            )
                          : paymentInfo.total}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Payment Summary */}
        <PaymentSummary
          total={paymentInfo.total}
          originalPrice={paymentInfo.originalPrice}
          itemCount={paymentInfo.itemCount}
          currency={paymentInfo.currency}
          onPayNow={handlePayNow}
          disabled={isEmpty}
          onRefundDetails={() => setShowRefundModal(true)}
          showRefundDetailsLink
          payLabel={isBankTransferOnly ? t('Continue', '계속하기') : undefined}
          note={
            isMixedPaymentSelection
              ? t(
                  'A bank-transfer workshop is selected together with card-payment workshops. Please book it separately.',
                  '계좌이체 워크숍이 카드 결제 워크숍과 함께 선택되었습니다. 따로 예약해 주세요.',
                )
              : isBankTransferOnly
                ? t(
                    'This workshop is paid by bank transfer. You will get the account details in the next step.',
                    '이 워크숍은 계좌이체로 결제합니다. 다음 단계에서 입금 계좌를 안내드립니다.',
                  )
                : ''
          }
        />
      </div>

      <RefundModal
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        content={selectedRefundPolicy}
      />

      {/* Remove Toast */}
      {showRemoveToast && (
        <>
          {/* Desktop Toast - Bottom Center */}
          <div className='hidden tabletH:fixed tabletH:bottom-[24px] tabletH:left-1/2 tabletH:-translate-x-1/2 tabletH:z-[1000] tabletH:block'>
            <Toast
              visibleOn='desktop'
              type='success'
              state='white'
              hierarchy='mostImportant'
              title={t('Items removed from cart', '장바구니에서 삭제되었습니다')}
              message=''
              onClose={() => {
                setShowRemoveToast(false);
                setRemovedItems([]);
              }}
              buttonLabel={t('Undo', '되돌리기')}
              onButtonClick={handleUndo}
              direction='up'
              customWidth='auto'
              autoHide
              autoHideDelay={10000}
            />
          </div>

          {/* Mobile Toast - Bottom Center */}
          <div className='tabletH:hidden fixed bottom-[24px] left-0 right-0 px-4 z-[9999999] flex justify-center'>
            <Toast
              visibleOn='mobile'
              type='success'
              state='white'
              hierarchy='important'
              title={t('Items removed from cart', '장바구니에서 삭제되었습니다')}
              message=''
              onClose={() => {
                setShowRemoveToast(false);
                setRemovedItems([]);
              }}
              direction='up'
              customWidth='auto'
              autoHide
              autoHideDelay={10000}
            />
          </div>
        </>
      )}
    </>
  );
};

export default Booking;
